import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { formatNumbers } from '../../../helpers/utils';
import get from 'lodash/get';
import { connect, useSelector } from 'react-redux';
import { makeApiCall, processApiValues, isWithinTradingHours } from '../../../helpers/utils';
import { useInterval } from '../../../helpers/custom-hooks';
import './budget-stocks-in-focus.m.css';

const BudgetStocksInFocus = ({
  widget,
  mobile = false,
  isDesktop = false,
  count = 5,
  customHeadlineClass = '',
  darkMode = false,
  lightMode = false,
  client = '',
  clients,
}) => {
  const widgetParam = widget ? '&view=apps' : '';
  const [currentStock, setCurrentStock] = useState('NSE');
  const [currentTab, setCurrentTab] = useState('most_active');
  const [currentStockValues, setCurrentStockValues] = useState([]);
  const [currentIndicesValues, setCurrentIndicesValues] = useState([]);
  const [initialCall, setInitialCall] = useState(true);
  const [isMobile, setIsMobile] = useState(isDesktop || mobile);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  useEffect(() => {
    if (!mobile) {
      const handleResize = () => {
        const width = window.innerWidth;
        setIsMobile(width <= 767);
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  });

  useEffect(() => {
    if (initialCall && inView) {
      getIndicesData();
      getStockFocusData(currentStock);
      setInitialCall(false);
    }
  }, [inView]);

  useInterval(() => {
    if (isWithinTradingHours(tradingHolidays, specialTradingDays) && inView) {
      getIndicesData();
      getStockFocusData(currentStock);
    }
  }, 30000);

  const getStockFocusData = async (stock, tab = '') => {
    const url = '/foapi/market/getStockInsight?limit=5';
    const mapping = {
      NSE: 'NIFTY%20500',
      BSE: 'BSE%20-%20500',
    };
    const tableMapping = {
      most_active: '2',
      gainers: '1',
      losers: '0',
    };

    if (widget && isMobile) {
      try {
        const res = await makeApiCall(
          `${url}&group=${mapping[stock]}&type=${tableMapping[tab] || tableMapping[currentTab]}`,
        );
        setCurrentStockValues(processApiValues([res]));
      } catch (error) {
        setCurrentStockValues([]);
      }
    } else {
      try {
        const res1 = await makeApiCall(url + '&group=' + mapping[stock] + '&type=0');
        const res2 = await makeApiCall(url + '&group=' + mapping[stock] + '&type=1');
        const res3 = await makeApiCall(url + '&group=' + mapping[stock] + '&type=2');
        setCurrentStockValues(processApiValues([res1, res2, res3]));
      } catch (error) {
        setCurrentStockValues([]);
      }
    }
  };

  const getIndicesData = async () => {
    const url = '/foapi/market/getIndices';
    try {
      const res = await makeApiCall(url);
      setCurrentIndicesValues(processApiValues([res]));
    } catch (e) {
      setCurrentStockValues([]);
    }
  };

  const getDataOnTabChange = (tab) => {
    if (currentTab !== tab) {
      setCurrentTab(tab);
      getStockFocusData(currentStock, tab);
    }
  };

  const activeColumnTitle = () => {
    const imgSrc =
      (isMobile && currentTab === 'most_active') || !isMobile
        ? 'fire_icon.svg'
        : 'fire_icon_grey.svg';
    return (
      <div
        styleName={`title-ctx ${currentTab === 'most_active' ? 'active' : ''}`}
        onClick={() => getDataOnTabChange('most_active')}
      >
        <img styleName="image" src={`/icons/${imgSrc}`} alt="Fire Icon" />
        <div styleName="dm-image" />
        <span styleName="title">Most Active</span>
      </div>
    );
  };

  const gainerColumnTitle = () => {
    const imgSrc =
      (isMobile && currentTab === 'gainers') || !isMobile
        ? 'arrow-top-right-thick.svg'
        : 'arrow-top-right-thick-grey.svg';
    return (
      <div
        styleName={`title-ctx custom ${currentTab === 'gainers' ? 'active' : ''}`}
        onClick={() => getDataOnTabChange('gainers')}
      >
        <img styleName="image" src={`/icons/${imgSrc}`} alt="Gainers Icon" />
        <div styleName="dm-image" />
        <span styleName="title">Gainers</span>
      </div>
    );
  };

  const loserColumnTitle = () => {
    const imgSrc =
      (isMobile && currentTab === 'losers') || !isMobile
        ? 'arrow-bottom-right-thick.svg'
        : 'arrow-bottom-right-thick-grey.svg';
    return (
      <div
        styleName={`title-ctx custom ${currentTab === 'losers' ? 'active' : ''}`}
        onClick={() => getDataOnTabChange('losers')}
      >
        <img styleName="image" src={`/icons/${imgSrc}`} alt="Losers Icon" />
        <div styleName="dm-image" />
        <span styleName="title">Losers</span>
      </div>
    );
  };

  const allTitles = () => {
    return (
      <>
        {activeColumnTitle()}
        {gainerColumnTitle()}
        {loserColumnTitle()}
      </>
    );
  };

  const createTable = (currentValues, index, length) => {
    const values = index === null || (widget && isMobile) ? currentValues[0] : currentValues[index];
    const clientWithUrl = !!client && clients.hasOwnProperty(client);
    const url =
      clientWithUrl && index === null
        ? clients[client]
        : widget
          ? `https://www.ndtvprofit.com/markets?src=stocks_in_focus_widget${widgetParam}`
          : `/markets?src=stocks_in_focus_widget${widgetParam}`;

    if (currentValues?.length > 0) {
      return (
        <div
          styleName={`data-list ${!!client ? 'client-view' : ''} ${clientWithUrl && index === null ? 'client-view-with-url' : ''}`}
        >
          {values?.slice(0, length).map((val, i) => (
            <a href={clientWithUrl && index === null ? url[i] : url} target="_blank" key={i}>
              <div
                styleName={`item-ctx ${isMobile ? 'mb-item-ctx' : ''} ${isMobile && index === null ? 'indices-new' : ''}`}
                style={{
                  background:
                    isMobile && index !== null
                      ? '#FFFFFF'
                      : val.change < 0
                        ? 'transparent linear-gradient(46deg, #fff 60%, #fcecf1) 0 0 no-repeat padding-box'
                        : 'transparent linear-gradient(47deg, #FFFFFF 0%, #ECFBF6 100%) 0 0 no-repeat padding-box',
                }}
              >
                {index === null && (
                  <div styleName="indices">
                    {(val.change === 0 || val.change > 0) && (
                      <img src="/icons/indice-positive.svg" alt="Positive Icon" />
                    )}
                    {val.change < 0 && <img src="/icons/indice-negative.svg" alt="Negative Icon" />}
                  </div>
                )}
                {isMobile && index === null ? (
                  <div styleName="item">
                    <div styleName="first-row">
                      {val.name}&nbsp;&nbsp;
                      {(!client || clientWithUrl) && (
                        <img
                          src="/icons/ic_chevron_right_grey.svg"
                          alt="Right Icon"
                          styleName="arrow-right"
                        />
                      )}
                    </div>
                    <div styleName="second-row">{formatNumbers(val.lastPrice, false)}</div>
                    <div
                      styleName={`third-row ${val.change === 0 || val.change > 0 ? 'positive' : 'negative'}`}
                    >
                      {formatNumbers(val.change, true)}&nbsp;&nbsp;({val.changePer.toFixed(2)}%)
                    </div>
                  </div>
                ) : (
                  <>
                    <div styleName="item">
                      <div styleName="first-row">
                        <span styleName="stock-name">{val.name}</span>
                        <span
                          styleName={`change-price ${
                            val.change === 0 || val.change > 0 ? 'positive' : 'negative'
                          }`}
                        >
                          {formatNumbers(val.change, true)}
                        </span>
                      </div>
                      <div styleName="second-row">
                        <span styleName="latest-price">{formatNumbers(val.lastPrice, false)}</span>
                        <span
                          styleName={`change-perc ${
                            val.change === 0 || val.change > 0 ? 'positive' : 'negative'
                          }`}
                        >
                          {val.changePer.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                    {(!client || (clientWithUrl && index === null)) && (
                      <div styleName="arrow">
                        {index === null && !isMobile && (
                          <img
                            styleName="right"
                            src="/icons/ic_chevron_right_24px.svg"
                            alt="Right Icon"
                          />
                        )}
                        {(index !== null || isMobile) && (
                          <img src="/icons/ic_chevron_right_grey.svg" alt="Right Icon" />
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </a>
          ))}
        </div>
      );
    } else {
      return (
        <div styleName="data-list">
          {Array.apply(null, Array(length)).map((id) => (
            <div
              key={id}
              styleName={`item-ctx ${index === null ? 'placeholder-ctx' : ''} ${isMobile && index === null ? 'indices-new' : ''} ${mobile || widget ? 'widget-view' : ''}`}
            >
              {index === null && (
                <div styleName="indices">
                  <img src="/icons/image_loading_icon.svg" alt="Loading Icon" />
                </div>
              )}
              <div styleName="item">
                <div styleName="first-row">
                  <div styleName="loading-shimmer-left"></div>
                  <div styleName="loading-shimmer-right"></div>
                </div>
                <div styleName="second-row">
                  <div styleName="loading-shimmer-left"></div>
                  <div styleName="loading-shimmer-right"></div>
                </div>
                <div styleName="third-row"></div>
              </div>
              <div styleName="arrow">
                {index === null && !isMobile && (
                  <img styleName="right" src="/icons/ic_chevron_right_24px.svg" alt="Right Icon" />
                )}
                {(index !== null || isMobile) && (
                  <img src="/icons/ic_chevron_right_grey.svg" alt="Right Icon" />
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  const updateStock = (stock) => {
    setCurrentStockValues([]);
    setCurrentStock(stock);
    getStockFocusData(stock);
  };

  return (
    <div
      ref={ref}
      styleName={`stocks-in-focus-ctx ${isMobile && !isDesktop ? 'section-fluid' : ''} ${widget && !lightMode ? 'widget-view' : ''} ${customHeadlineClass} ${darkMode ? 'dark-mode' : ''} ${lightMode ? 'light-mode' : ''}`}
    >
      <div styleName={`head-line ${isMobile ? 'mb-head-line' : ''}`}>
        <div styleName="heading">
          <img src="/icons/stocksinfocus.svg" alt="Title Logo" />
          <span styleName="title">{`Stocks ${mobile || widget ? 'Today' : 'In Focus'}`}</span>
        </div>
        <div styleName="toggles">
          <div
            styleName={`toggle ${currentStock === 'NSE' ? 'active' : ''}`}
            onClick={() => updateStock('NSE')}
          >
            NSE
          </div>
          <div
            styleName={`toggle ${currentStock === 'BSE' ? 'active' : ''}`}
            onClick={() => updateStock('BSE')}
          >
            BSE
          </div>
        </div>
      </div>
      <div
        styleName={`stock-list-ctx ${isMobile ? 'mb-stock-list-ctx two-collection-widget' : ''}`}
      >
        <div
          styleName={`indices-ctx ${isMobile ? 'mb-indices-ctx' : ''} ${!!currentIndicesValues && currentIndicesValues.length === 0 ? 'loading' : ''}`}
        >
          <div styleName="stock-table">{createTable(currentIndicesValues, null, 2)}</div>
          <hr styleName="partition" />
        </div>
        {isMobile && <div styleName="all-titles">{allTitles()}</div>}
        <div
          styleName={`stock-tables ${isMobile ? 'mb-stock-tables' : ''} ${currentStockValues.length === 0 ? 'loading' : ''}`}
        >
          <div
            styleName={`stock-table-ctx ${isMobile && currentTab !== 'most_active' ? 'hide' : ''}`}
          >
            {!isMobile && activeColumnTitle()}
            <div styleName="stock-table">{createTable(currentStockValues, 2, count)}</div>
          </div>
          <div styleName={`stock-table-ctx ${isMobile && currentTab !== 'gainers' ? 'hide' : ''}`}>
            {!isMobile && gainerColumnTitle()}
            <div styleName="stock-table">{createTable(currentStockValues, 1, count)}</div>
          </div>
          <div styleName={`stock-table-ctx ${isMobile && currentTab !== 'losers' ? 'hide' : ''}`}>
            {!isMobile && loserColumnTitle()}
            <div styleName="stock-table">{createTable(currentStockValues, 0, count)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  clients: get(state.qt, ['config', 'features', 'budget', 'clients'], {}),
});

export default connect(mapStateToProps)(BudgetStocksInFocus);
